import { rectSideAnchor, routeManualConnector, routeIntersectsRect } from './edgeRouting';

export const CONNECTOR_HOVER_DELAY = 450;
const close = (a, b) => Math.abs(a - b) < 0.001;
const horizontalSide = side => side === 'left' || side === 'right';

export function connectorSegments(route) {
  if (!['rounded', 'branch'].includes(route?.kind) || !Array.isArray(route.points)) return [];
  return route.points.slice(1).flatMap((end, index) => {
    const start = route.points[index];
    const horizontal = close(start.y, end.y);
    if (!horizontal && !close(start.x, end.x)) return [];
    if (Math.hypot(end.x - start.x, end.y - start.y) < 30) return [];
    return [{ index, start, end, axis: horizontal ? 'y' : 'x' }];
  });
}

// Use the rendered straight section; stop before its rounded corners.
export function connectorSegmentPath(route, index, inset = 0) {
  const start = route?.points?.[index], end = route?.points?.[index + 1];
  if (!start || !end || !Array.isArray(route.segments)) return '';
  const axis = close(start.y, end.y) ? 'y' : 'x';
  const along = axis === 'x' ? 'y' : 'x';
  const low = Math.min(start[along], end[along]), high = Math.max(start[along], end[along]);
  const at = route.segments.findIndex(segment => close(segment.from[axis], start[axis])
    && close(segment.to[axis], start[axis])
    && segment.from[along] >= low - 0.001 && segment.from[along] <= high + 0.001
    && segment.to[along] >= low - 0.001 && segment.to[along] <= high + 0.001);
  if (at < 0) return '';
  const straight = route.segments[at];
  const length = Math.abs(straight.to[along] - straight.from[along]);
  const margin = Math.min(Math.max(0, inset), length / 4);
  const direction = Math.sign(straight.to[along] - straight.from[along]);
  const from = { ...straight.from, [along]: straight.from[along] + margin * direction };
  const to = { ...straight.to, [along]: straight.to[along] - margin * direction };
  return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
}

export function moveConnectorSegment(route, index, delta) {
  if (!Number.isFinite(delta) || !route?.points?.[index + 1]) return null;
  const points = route.points.map(point => ({ ...point }));
  const start = points[index], end = points[index + 1];
  const axis = close(start.y, end.y) ? 'y' : 'x';
  if (Math.abs(delta) < 0.001) return route;
  const movedStart = { ...start, [axis]: start[axis] + delta };
  const movedEnd = { ...end, [axis]: end[axis] + delta };
  const along = axis === 'x' ? 'y' : 'x';
  const direction = Math.sign(end[along] - start[along]);
  const lead = Math.min(24, Math.abs(end[along] - start[along]) / 3);
  const replacement = [];
  if (index === 0) {
    const anchorLead = { ...start, [along]: start[along] + direction * lead };
    replacement.push(start, anchorLead, { ...anchorLead, [axis]: movedStart[axis] });
  } else replacement.push(movedStart);
  if (index === points.length - 2) {
    const anchorLead = { ...end, [along]: end[along] - direction * lead };
    replacement.push({ ...anchorLead, [axis]: movedEnd[axis] }, anchorLead, end);
  } else replacement.push(movedEnd);
  return routeManualConnector([...points.slice(0, index), ...replacement, ...points.slice(index + 2)], route);
}

export function connectorDragTargets(routes, key, index, style) {
  const selected = routes.get(key);
  const segment = connectorSegments(selected).find(entry => entry.index === index);
  if (!segment) return [];
  if (style !== 'branch') return [{ key, route: selected, index }];
  const parent = key.split('::')[0];
  const targets = [];
  routes.forEach((route, candidateKey) => {
    if (candidateKey.split('::')[0] !== parent || route.side !== selected.side) return;
    const match = connectorSegments(route).find(candidate => candidate.axis === segment.axis
      && close(candidate.start[segment.axis], segment.start[segment.axis]));
    if (match) targets.push({ key: candidateKey, route, index: match.index });
  });
  return targets;
}

export function connectorControls(route, parentId, style) {
  return { version: 1, parentId, style, side: route.side, targetSide: route.targetSide, points: route.points.map(point => ({ ...point })) };
}

export function applyConnectorControls(route, controls, parentId, style) {
  if (!route || controls?.version !== 1 || controls.parentId !== parentId || controls.style !== style
    || !['rounded', 'branch'].includes(style) || !Array.isArray(controls.points) || controls.points.length < 2) return route;
  if (!['left', 'right', 'top', 'bottom'].includes(controls.side)
    || !['left', 'right', 'top', 'bottom'].includes(controls.targetSide)) return route;
  const points = controls.points.map(point => ({ ...point }));
  const from = rectSideAnchor(route.sourceRect, controls.side);
  const to = rectSideAnchor(route.targetRect, controls.targetSide);
  const start = points[0], end = points[points.length - 1];
  const dx = from.x - start.x, dy = from.y - start.y;
  if (close(to.x - end.x, dx) && close(to.y - end.y, dy)) {
    points.forEach(point => { point.x += dx; point.y += dy; });
  } else if (points.length > 2) {
    points[0] = from; points[points.length - 1] = to;
    points[1][horizontalSide(controls.side) ? 'y' : 'x'] = from[horizontalSide(controls.side) ? 'y' : 'x'];
    points[points.length - 2][horizontalSide(controls.targetSide) ? 'y' : 'x'] = to[horizontalSide(controls.targetSide) ? 'y' : 'x'];
  } else return route;
  return routeManualConnector(points, { ...route, side: controls.side, targetSide: controls.targetSide }) || route;
}

export function connectorAvoidsNodes(route, nodes, parentId, childId) {
  return !!route && !nodes.some(node => node.id !== parentId && node.id !== childId
    && routeIntersectsRect(route, node.rect || node));
}

export function createConnectorEditCommand(nodes, edits, style) {
  const byId = new Map(nodes.map(node => [node.id, node]));
  return {
    type: 'edit-connectors',
    connectors: edits.map(({ childId, parentId, route }) => {
      if (!byId.has(childId)) throw new Error('Connector endpoint no longer exists');
      return { id: childId, connector: route ? connectorControls(route, parentId, style) : null };
    }),
  };
}
